import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import Logger from "../../../services/Logger";
import React, { useState } from "react";
import Item from "../../components/Item";
import GuardianService from "./GuardianService";

export default function GuardianMain() {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [migrating, setMigrating] = useState(false);
    const [migrationResult, setMigrationResult] = useState(null);

    const handleGetAllSections = async () => {
        setLoading(true);
        setError(null);
        setMigrationResult(null);
        
        const result = await GuardianService.getAllSections();
        
        if (result.success) {
            setSections(result.data);
        } else {
            setError(`Failed to fetch sections: ${result.error}`);
            Logger.error('Error fetching Guardian sections:', result.error);
        }
        
        setLoading(false);
    };

    const handleMigrateSections = async () => {
        if (sections.length === 0) {
            setError('Please fetch sections first before migrating');
            return;
        }

        setMigrating(true);
        setError(null);
        setMigrationResult(null);
        
        const result = await GuardianService.migrateSections(sections);
        
        if (result.success) {
            setMigrationResult(result);
        } else {
            setError(`Migration failed: ${result.error}`);
            Logger.error('Error migrating Guardian sections:', result.error);
        }
        
        setMigrating(false);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2} sx={{ justifyContent: "center" }}>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Typography variant="h5" sx={{ mb: 2 }}>
                                Guardian API
                            </Typography>
                            
                            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                <Button 
                                    variant="contained" 
                                    onClick={handleGetAllSections}
                                    disabled={loading || migrating}
                                >
                                    {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                                    Get All Sections
                                </Button>
                                
                                <Button 
                                    variant="contained" 
                                    color="secondary"
                                    onClick={handleMigrateSections}
                                    disabled={loading || migrating || sections.length === 0}
                                >
                                    {migrating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                                    Migrate
                                </Button>
                                
                                {sections.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Found {sections.length} sections
                                    </Typography>
                                )}
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {migrationResult && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        Migration completed! {migrationResult.summary.successful} successful, {migrationResult.summary.failed} failed out of {migrationResult.summary.total} sections.
                                    </Alert>
                                    
                                    {migrationResult.summary.failed > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="h6" sx={{ mb: 1 }}>
                                                Failed Migrations:
                                            </Typography>
                                            <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
                                                {migrationResult.results
                                                    .filter(result => result.status === 'error')
                                                    .map((result, index) => (
                                                        <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                                                            {result.section}: {result.error}
                                                        </Typography>
                                                    ))
                                                }
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {sections.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Guardian Sections
                                    </Typography>
                                    
                                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>ID</strong></TableCell>
                                                    <TableCell><strong>Web Title</strong></TableCell>
                                                    <TableCell><strong>Web URL</strong></TableCell>
                                                    <TableCell><strong>API URL</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sections.map((section) => (
                                                    <TableRow key={section.id} hover>
                                                        <TableCell>{section.id}</TableCell>
                                                        <TableCell>{section.webTitle}</TableCell>
                                                        <TableCell>
                                                            <a 
                                                                href={section.webUrl} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#1976d2', textDecoration: 'none' }}
                                                            >
                                                                {section.webUrl}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell>
                                                            <a 
                                                                href={section.apiUrl} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#1976d2', textDecoration: 'none' }}
                                                            >
                                                                {section.apiUrl}
                                                            </a>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </Box>
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
}
